import type { RefObject } from 'react'
import { gsap, ScrollTrigger, useGSAP, registerMotion, NO_PREF } from './gsap'

/** Path length in user units; 0 when the browser cannot measure it (never throws). */
function lengthOf(p: SVGGeometryElement): number {
  try {
    const len = p.getTotalLength()
    return Number.isFinite(len) ? len : 0
  } catch {
    return 0
  }
}

/** Hides a solid path behind a dash as long as itself, ready to be drawn by tweening the offset to 0. */
function prepareDraw(p: SVGGeometryElement): boolean {
  const len = lengthOf(p)
  if (!len) return false
  gsap.set(p, { strokeDasharray: len, strokeDashoffset: len })
  return true
}

/** Seconds for the ripple to reach the farthest comune. */
const RIPPLE_S = 2.8

/** A comune's dot circles and ripple, and its distance from the base. */
interface Dot {
  circles: SVGCircleElement[]
  ripple: SVGCircleElement | null
  centre: { x: number; y: number } | null
}

/** The dot group's own `translate(x y)`: its centre in the map's user space. */
function centreOf(g: SVGGElement): { x: number; y: number } | null {
  const list = g.transform.baseVal
  if (!list.numberOfItems) return null
  const m = list.getItem(0).matrix
  return { x: m.e, y: m.f }
}

/** A comune appears: its dot grows in, and one ring spreads from it. */
function pop(a: Dot) {
  if (a.circles.length) gsap.to(a.circles, { scale: 1, duration: 0.6, ease: 'back.out(2)' })
  if (a.ripple) {
    const r = Number(a.ripple.getAttribute('r')) || 4
    gsap.fromTo(a.ripple, { attr: { r }, opacity: 0.9 }, { attr: { r: r * 4.5 }, opacity: 0, duration: 1.6, ease: 'sine.out' })
  }
}

/**
 * The zone map's ripple (see useDrawPath). Must run inside the hook's
 * matchMedia context.
 */
function rippleOut(svg: SVGSVGElement) {
  const dots: Dot[] = Array.from(svg.querySelectorAll<SVGGElement>('g[data-map-dot]')).map((g) => ({
    circles: Array.from(g.querySelectorAll<SVGCircleElement>('circle')),
    ripple: g.parentElement?.querySelector<SVGCircleElement>('[data-map-ripple]') ?? null,
    centre: centreOf(g),
  }))
  if (!dots.length) return
  // The base is the dot with its ring (two circles).
  const base = dots.find((d) => d.circles.length > 1)?.centre ?? dots[0].centre
  const dist = (d: Dot) => (base && d.centre ? Math.hypot(d.centre.x - base.x, d.centre.y - base.y) : 0)
  const far = Math.max(1, ...dots.map(dist))
  gsap.set(dots.flatMap((d) => d.circles), { scale: 0, transformOrigin: '50% 50%' })

  const tl = gsap.timeline({ paused: true })
  // Easing out on distance: the plain around the base fills quickly, the Valsesia follows.
  dots.forEach((d) => tl.call(() => pop(d), [], RIPPLE_S * Math.sqrt(dist(d) / far)))
  // onEnterBack too: a page reloaded below the map comes back into it from
  // below and must not leave the dots hidden. play() on a finished timeline does nothing.
  const release = () => void tl.play()
  ScrollTrigger.create({ trigger: svg, start: 'top 75%', onEnter: release, onEnterBack: release })
}

/**
 * Lines drawn as their section scrolls through (scrubbed, `ease: 'none'`):
 *
 * - `path[data-draw]` (any line in a `preserveAspectRatio="none"` svg,
 *   e.g. `M 0 6 L 1000 6`): the
 *   classic dasharray = length, dashoffset length → 0. The length is in user
 *   units, like the dash. The scrub runs over the nearest `[data-draw-scope]`
 *   ancestor when there is one (the profile's own frame, so the ridge
 *   finishes while the figure is still in view), else over the section.
 * - The zone map ripples out of the base, once, when it is 75 % up the
 *   viewport: every `g[data-map-dot]`'s circles start at scale 0 and pop in
 *   order of distance from the base (the dot with the ring), over RIPPLE_S,
 *   each throwing one `[data-map-ripple]`. GSAP bakes the dot's centre into
 *   the matrix it writes to the circles' `transform`.
 *
 * Reduced motion: nothing — every path is fully drawn and every dot whole in
 * the markup, the ripples stay at their markup opacity 0.
 */
export function useDrawPath(scope: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      registerMotion()
      if (!scope.current?.querySelector('path[data-draw], [data-map-dot]')) return
      const mm = gsap.matchMedia()
      mm.add(NO_PREF, () => {
        gsap.utils.toArray<SVGPathElement>('path[data-draw]').forEach((p) => {
          if (!prepareDraw(p)) return
          gsap.to(p, {
            strokeDashoffset: 0,
            ease: 'none',
            scrollTrigger: { trigger: p.closest('[data-draw-scope], section') ?? p, start: 'top 75%', end: 'bottom 65%', scrub: 0.6 },
          })
        })

        const maps = new Set<SVGSVGElement>()
        gsap.utils.toArray<SVGElement>('[data-map-dot]').forEach((el) => {
          if (el.ownerSVGElement) maps.add(el.ownerSVGElement)
        })
        maps.forEach(rippleOut)
      })
    },
    { scope },
  )
}
